import { type FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';

import type { SummaryContractNotificationsTextualData } from './summary-step.types';
import {
  checkOfferIncludesDynamicPricingProduct,
  checkOfferIncludesNonDynamicPricingProduct,
} from '../../helpers';
import { checkOffersIncludeVariablePricingProduct } from '../../helpers/misc';
import type { Offer } from '../../types';

interface NotificationFlagsOptions {
  /** Retention skips the variable-pricing product guard used on the sales summary. */
  skipVariablePricingCheck?: boolean;
}

export const getSummaryContractNotificationFlags = (
  selectedOffer: Offer | undefined,
  notifications?: SummaryContractNotificationsTextualData,
  options?: NotificationFlagsOptions,
) => {
  const passesVariableGuard =
    options?.skipVariablePricingCheck || !checkOffersIncludeVariablePricingProduct(selectedOffer);

  return {
    shouldShowDynamicPricingContractNotification:
      passesVariableGuard &&
      checkOfferIncludesDynamicPricingProduct(selectedOffer) &&
      !checkOfferIncludesNonDynamicPricingProduct(selectedOffer) &&
      !!notifications?.dynamicPricingContractNotification?.value?.content,
    shouldShowHybridContractNotification:
      passesVariableGuard &&
      checkOfferIncludesDynamicPricingProduct(selectedOffer) &&
      checkOfferIncludesNonDynamicPricingProduct(selectedOffer) &&
      !!notifications?.hybridContractNotification?.value?.content,
  };
};

interface Props {
  selectedOffer?: Offer;
  notifications?: SummaryContractNotificationsTextualData;
  skipVariablePricingCheck?: boolean;
}

export const SummaryContractNotifications: FC<Props> = ({
  selectedOffer,
  notifications,
  skipVariablePricingCheck,
}) => {
  const { shouldShowDynamicPricingContractNotification, shouldShowHybridContractNotification } =
    getSummaryContractNotificationFlags(selectedOffer, notifications, { skipVariablePricingCheck });

  return (
    <>
      {shouldShowDynamicPricingContractNotification && (
        <TrackedNotificationBox
          isAlert={false}
          label={notifications?.dynamicPricingContractNotification?.value?.title}
          title={notifications?.dynamicPricingContractNotification?.value?.title ?? ''}
          text={<RichText html={notifications?.dynamicPricingContractNotification?.value?.content} />}
          variant={notifications?.dynamicPricingContractNotification?.value?.variant}
        />
      )}

      {shouldShowHybridContractNotification && (
        <TrackedNotificationBox
          isAlert={false}
          label={notifications?.hybridContractNotification?.value?.title}
          title={notifications?.hybridContractNotification?.value?.title ?? ''}
          text={<RichText html={notifications?.hybridContractNotification?.value?.content} />}
          variant={notifications?.hybridContractNotification?.value?.variant}
        />
      )}
    </>
  );
};
